import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTeamContext } from '@/contexts/TeamContext'
import { useUserInit } from '@/contexts/UserInitContext'
import { TEAM_GATE_CODES, teamRefusal } from '@/utils/teamSeats'
import toast from 'react-hot-toast'

/**
 * Hook to automatically accept pending team invitations after OAuth authentication
 *
 * Flow:
 * 1. User clicks invite link → /team/join/:token
 * 2. JoinTeamPage stores token in sessionStorage as 'pendingTeamInvite'
 * 3. User authenticates via Clerk OAuth
 * 4. Clerk redirects to default location (not back to join page)
 * 5. UserInitProvider creates the user record in the database
 * 6. This hook detects the pending invite and auto-accepts it
 * 7. User is redirected to dashboard with success toast
 *
 * Usage: Add to main App component that renders after authentication
 */
export function usePendingTeamInvite() {
  const { isLoaded, isSignedIn } = useAuth()
  const { isInitialized } = useUserInit()
  const navigate = useNavigate()
  const location = useLocation()
  const { acceptInvite, refetchAllTeams } = useTeamContext()
  const [accepting, setAccepting] = useState(false)
  const [accepted, setAccepted] = useState(false)

  useEffect(() => {
    const acceptPendingInvite = async () => {
      // Only run once user is authenticated AND initialized in database
      if (!isLoaded || !isSignedIn || !isInitialized) {
        return
      }

      // Check if already processing in this session
      if (accepted || accepting) {
        return
      }

      // Don't run if user is already on the join page (let that page handle it)
      if (location.pathname.startsWith('/team/join/')) {
        return
      }

      // Check for pending invite in sessionStorage
      const pendingToken = sessionStorage.getItem('pendingTeamInvite')
      if (!pendingToken) {
        return
      }

      console.log('🔄 [usePendingTeamInvite] Found pending team invite, auto-accepting...')

      // Clear the token immediately to prevent duplicate attempts
      sessionStorage.removeItem('pendingTeamInvite')
      setAccepting(true)

      try {
        await acceptInvite(pendingToken)

        // Refetch teams to update context
        await refetchAllTeams()

        console.log('✅ [usePendingTeamInvite] Successfully joined team')
        setAccepted(true)

        // Show success message
        toast.success('Welcome! You have successfully joined the team.')

        // Navigate to dashboard
        navigate('/dashboard', { replace: true })
      } catch (error: any) {
        console.error('❌ [usePendingTeamInvite] Failed to accept invite:', error)

        // Handle specific error cases. The code is read before the status:
        // a team with no plan that cannot take this person says so with a
        // code and its own sentence, and a status alone once read that as
        // "already a member" and sent the person on as if they had got in.
        const refusal = teamRefusal(error)
        if ((refusal.code === TEAM_GATE_CODES.FREE_SEAT_TAKEN || refusal.code === TEAM_GATE_CODES.PLAN_REQUIRED) && refusal.sentence) {
          toast.error(refusal.sentence)
        } else if (error?.response?.status === 409) {
          toast.error('You are already a member of this team')
          navigate('/dashboard', { replace: true })
        } else if (error?.response?.status === 400) {
          toast.error('Team is at maximum capacity')
        } else if (error?.response?.status === 404) {
          toast.error('This invitation has expired or is invalid')
        } else {
          toast.error('Failed to join team. Please try the invite link again.')
        }
      } finally {
        setAccepting(false)
      }
    }

    acceptPendingInvite()
  }, [isLoaded, isSignedIn, isInitialized, location.pathname, accepted, accepting, acceptInvite, refetchAllTeams, navigate])

  return {
    accepting,
    accepted
  }
}
